<template>
  <div>
    <div :class="containerClass">
      <div class="tw-flex tw-flex-col">
        <h1 class="page-title heading-color">
          {{ pageTitle }}
        </h1>
        <SelectFavoriteListDropdown
          v-if="showSelectFavoritListDropdown"
          class="tw-mt-2 tw-ml-0.5"
          link-class-name="tw-text-lg"
          z-index="tw-z-25"
        />
      </div>
      <div
        :class="buttonContainerClass"
      >
        <Button
          v-if="showMapButton"
          v-bind="mapButtonProps"
        >
          <font-awesome-icon
            :icon="['fas', 'globe-americas']"
          />
          <span :class="buttonTextClass">View Map</span>
        </Button>
        <Button
          v-if="showListButton"
          v-bind="listButtonProps"
        >
          <font-awesome-icon
            :icon="['fas', 'list']"
          />
          <span :class="buttonTextClass">View List</span>
        </Button>
        <Button
          v-if="showEditButton"
          v-bind="editButtonProps"
        >
          <font-awesome-icon
            class="action-button-icon tw-mr-0"
            :icon="['fas', 'pencil-alt']"
          />
          <span :class="buttonTextClass">Edit</span>
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import { ButtonTypes } from '@@/components/Common/Button.vue';
import { addQueryParamsToPath } from '@@/utils/CommonUtils';
import { useUserFavoritesStore } from '@@/stores/UserFavorites';

export default {
  name: 'FavoritesHeader',

  props: {
    compareField: {
      type: Object,
      default: null,
    },
    isEditPage: {
      type: Boolean,
      default: false,
    },
    isMapPage: {
      type: Boolean,
      default: false,
    },
    pageTitle: {
      type: String,
      default: 'Favorites',
    },
  },

  computed: {
    ...mapState(useUserFavoritesStore, { activeList: 'getActiveList' }),
    ...mapState(useUserFavoritesStore, { favoriteIds: (state) => state.user.favoriteIds }),

    activeListName() {
      return this.activeList.name;
    },

    buttonContainerClass() {
      return 'tw-flex tw-items-start lg:tw-block lg:tw-text-center';
    },

    buttonTextClass() {
      return 'tw-hidden lg:tw-inline';
    },

    containerClass() {
      return 'tw-flex tw-justify-between tw-my-2 lg:tw-my-4';
    },

    editButtonProps() {
      // Navigate to correct favorite settings page based on current favorites page. e.g. When the
      // user is on /user/favorites/locations/summary navigate to
      // /user/settings/favorites/locations.

      const to = this.$route.path === '/user/favorites/map'
        ? '/user/settings/favorites#edit-favorites'
        : `/user/settings/favorites?view=${this.$route.path.split('/')[3]}#edit-favorites`;

      return {
        additionalClassNames: 'action-button',
        href: null,
        small: true,
        to,
        type: ButtonTypes.card,
      };
    },

    hasFavorites() {
      if (this.favoriteIds?.favorites?.cam_ids?.length) {
        return true;
      }

      if (this.favoriteIds?.favorites?.daily_read_ids?.length) {
        return true;
      }

      if (this.favoriteIds?.favorites?.location_ids?.length) {
        return true;
      }

      if (this.favoriteIds?.favorites?.season_pass_ids?.length) {
        return true;
      }

      if (this.favoriteIds?.favorites?.trail_map_ids?.length) {
        return true;
      }

      return false;
    },

    hasFavoriteLocations() {
      if (this.favoriteIds?.favorites?.location_ids?.length) {
        return true;
      }

      return false;
    },

    listButtonProps() {
      let to = '/user/favorites';

      // Navigate to correct favorite page based on current favorite settings page. e.g. When the
      // user is on /user/settings/favorites/locations/summary navigate to
      // /user/settings/favorites/locations.

      if (this.$route.path.match(/\/user\/settings\/favorites\/(locations|daily-snows|cams|season-passes|trail-maps)/)) {
        to = this.$route.path.replace(/\/settings/, '');
      }

      return {
        additionalClassNames: 'action-button',
        small: true,
        to,
        type: ButtonTypes.card,
      };
    },

    mapButtonProps() {
      const query = this.compareField ? { compare: this.compareField.field } : {};

      return {
        additionalClassNames: 'action-button',
        small: true,
        to: addQueryParamsToPath('/user/favorites/map', query),
        type: ButtonTypes.card,
      };
    },

    showEditButton() {
      return this.hasFavorites && !this.isEditPage;
    },

    showSelectFavoritListDropdown() {
      return !this.isEditPage;
    },

    showListButton() {
      return this.isEditPage || this.isMapPage;
    },

    showMapButton() {
      return this.hasFavoriteLocations && !this.isMapPage;
    },
  },
};
</script>
